/**
 * getExtensions — the single source of truth for the editor's extension
 * list. Called by `PageEditor.tsx` whenever the page tree changes (so
 * the PageLink `parse.updateDOM` resolver has the latest pages).
 *
 * CRITICAL: the dynamic data (`pageTree`, `members`) is passed via
 * **refs** rather than arrays. `useEditor(..., [extensions])` in
 * @tiptap/react destroys and recreates the editor when the extensions
 * array reference changes — which would clobber the user's cursor and
 * selection on every `usePage` refetch (TanStack Query returns a new
 * data object each time, cascading through memos). Reading from refs
 * keeps the extensions array stable for the editor's lifetime while
 * still surfacing fresh data to the suggestion plugins.
 */

import type { RefObject } from 'react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { Markdown } from 'tiptap-markdown'
import type { Page } from '@friday/shared/realtime'
import { PageLink } from './pageLinkNode'
import { PageLinkSuggestion } from './pageLinkSuggestion'
import { MentionWithPalette, type MentionItem } from './mentionExtension'
import { SlashCommand } from './slashExtension'
import { NotebookTable, TableCell, TableHeader, TableRow } from './tableExtension'

export interface MemberDirectoryEntry {
  userId: string
  displayName: string
  email?: string | null
}

export interface GetExtensionsOpts {
  wsId: string
  pageTree: RefObject<Page[]>
  members: RefObject<MemberDirectoryEntry[]>
  /** The current user's id, used to label "(you)" in the mention list. */
  currentUserId?: string
}

export function getExtensions(opts: GetExtensionsOpts) {
  return [
    Markdown.configure({
      transformPastedText: true,
      transformCopiedText: true,
      breaks: true,
    }),
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: { defaultLanguage: 'plaintext' },
    }),
    // Tables (FRIDAY-66). The row / cell / header nodes are declared
    // locally because `@tiptap/extension-table` v2 does not bundle them.
    NotebookTable,
    TableRow,
    TableCell,
    TableHeader,
    TaskList,
    TaskItem.configure({ nested: true }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { class: 'notebook-link', rel: 'noopener noreferrer nofollow' },
    }),
    Placeholder.configure({
      placeholder: ({ node }) => {
        if (node.type.name === 'heading') return 'Heading'
        return 'Type / for blocks, [[ to link a page, @ to mention someone'
      },
      showOnlyWhenEditable: true,
    }),
    PageLink.configure({ wsId: opts.wsId, getPageTree: () => opts.pageTree.current ?? [] }),
    PageLinkSuggestion.configure({ wsId: opts.wsId, getPageTree: () => opts.pageTree.current ?? [] }),
    MentionWithPalette.configure({
      HTMLAttributes: { class: 'mention-chip' },
      fetchItems: async (query: string): Promise<MentionItem[]> => {
        const q = query.toLowerCase()
        return (opts.members.current ?? [])
          .filter(
            (m) =>
              m.displayName.toLowerCase().includes(q) ||
              (m.email ?? '').toLowerCase().includes(q),
          )
          .slice(0, 8)
          .map((m) => ({ id: m.userId, label: m.displayName }))
      },
    }),
    SlashCommand,
  ]
}

